import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { TOCChild, TOCItem, TOCParent } from '~/common/types';
import layerConfigStore from '~/store2/layerConfig';
import layerStore from '~/store2/layers';
import { allSpecialDatasetNames } from '~/store2/specialDatasets';
import { UtilitySectionLabels } from '~/store2/TOCLayers';
import { persistCustomDataVisibility } from './customDataTOC.handlers';
import { collectLeaves } from './sharedUtils';

// ---------------------------------------------------------------------------
// Picker write path. These shared handlers route EVERY picker edit through the
// `layerConfigStore` facade rather than mutating `layerStore` directly.
// The facade branches once, internally, on the load state: not loaded mutates the shared
// `layerStore` directly; loaded mutates the doc-store's own editable set and mirrors it for
// render. This is what makes the persist source picker-only — the debounced
// sync observes the doc-store sets, so download/export/side-panel writes of the
// shared `layerStore` sets never trigger a PUT.
// ---------------------------------------------------------------------------

export const URBAN_BOUNDS = 'urbanAreaBoundaries';

export type UtilityPreset = 'utility_lines' | 'utility_points_and_polygons' | 'utility_services';

function isTOCParent(item: TOCItem): item is TOCParent {
  return 'children' in item;
}

/**
 * Index the document TOC's leaves by id for bookmark rendering, composing each leaf's label
 * the same way the legacy path does (`layerStore.addPinnedLayer`): when a leaf's immediate
 * parent is a utility section (`UtilitySectionLabels`) or a special dataset
 * (`allSpecialDatasetNames`), the label becomes `"Parent / Leaf"` (e.g. "Gas / Transmission
 * Lines") to disambiguate line types across utilities; otherwise it stays the bare leaf label.
 */
export function buildBookmarkLeafIndex(documentTOC: readonly TOCItem[]): Map<string, TOCChild> {
  const leafById = new Map<string, TOCChild>();

  const walk = (nodes: readonly TOCItem[], parentLabel?: string) => {
    for (const node of nodes) {
      if (isTOCParent(node)) {
        walk(node.children, node.label);
      } else {
        const label =
          parentLabel && (UtilitySectionLabels.has(parentLabel) || allSpecialDatasetNames.has(parentLabel))
            ? `${parentLabel} / ${node.label}`
            : node.label;
        leafById.set(node.id, { id: node.id, label });
      }
    }
  };

  walk(documentTOC);
  return leafById;
}

export function collectLeafIds(items: TOCItem[]): string[] {
  const ids: string[] = [];
  items.forEach((item) => {
    if (isTOCParent(item)) {
      ids.push(...collectLeafIds(item.children));
    } else {
      ids.push(item.id);
    }
  });
  return ids;
}

export function handleLayerLeafToggle(layerId: string, isVisible: boolean | 'indeterminate'): void {
  if (isVisible === 'indeterminate') return;
  // A tab-locked leaf renders disabled; guard here too so no other entry point can edit it.
  if (layerStore.lockedLeafIds.has(layerId)) return;
  capturePosthogEvent(POSTHOG_EVENTS.layerToggled, {
    layer_id: layerId,
    action: isVisible ? 'on' : 'off',
  });
  layerConfigStore.setLayerVisible(layerId, isVisible);
}

export function handleLayerSectionToggle(
  sectionLabel: string,
  value: boolean | 'indeterminate',
  visibleItemChildren: TOCItem[],
): void {
  if (value === 'indeterminate') return;
  // Tab-locked leaves are excluded from bulk edits — the active tab owns them.
  const childLayerIds = collectLeafIds(visibleItemChildren).filter((id) => !layerStore.lockedLeafIds.has(id));
  if (childLayerIds.length === 0) return;
  capturePosthogEvent(POSTHOG_EVENTS.layerSectionToggled, {
    section_label: sectionLabel,
    action: value ? 'on' : 'off',
    child_layer_ids: childLayerIds,
  });
  layerConfigStore.setSectionVisible(childLayerIds, value);
}

export function handleTurnOffAllLayers(persistProjectId?: string): void {
  capturePosthogEvent(POSTHOG_EVENTS.layersAllToggled, { action: 'off' });
  // Clears the shared render set wholesale, custom-data ids included.
  layerConfigStore.turnOffAll();
  persistCustomDataVisibility(persistProjectId);
}

export function handleTurnOnAllLayers(hiddenLeafIds?: ReadonlySet<string>, persistProjectId?: string): void {
  capturePosthogEvent(POSTHOG_EVENTS.layersAllToggled, { action: 'on' });
  layerConfigStore.turnOnAll(hiddenLeafIds);
  // turnOnAll walks document leaves only, so the custom-data group needs re-adding by hand —
  // without it "turn off all" would be the one gesture a user could not undo from this menu.
  // Only once the off-state is durable: `persistProjectId` is set exactly when it is, and
  // re-adding without it would move custom-data layers a user never asked this menu to touch.
  if (!persistProjectId) return;
  for (const leaf of collectLeaves(layerStore.customDataTOCGroup)) {
    layerStore.visibleLayerIds.add(leaf.id);
  }
  persistCustomDataVisibility(persistProjectId);
}

export function handleUrbanRuralToggle(): void {
  const action = layerStore.visibleLayerIds.has(URBAN_BOUNDS) ? 'off' : 'on';
  capturePosthogEvent(POSTHOG_EVENTS.layerToggled, {
    layer_id: URBAN_BOUNDS,
    action,
  });
  layerConfigStore.setLayerVisible(URBAN_BOUNDS, action === 'on');
}

const PRESET_LAYERS: Record<UtilityPreset, () => string[]> = {
  utility_lines: () => [...layerStore.utilityLineLayers],
  utility_points_and_polygons: () => [...layerStore.utilityPointsAndPolygonLayers],
  utility_services: () => [...layerStore.utilityServiceLayers],
};

export function handleUtilityPresetToggle(
  preset: UtilityPreset,
  action: 'on' | 'off',
  hiddenLeafIds?: ReadonlySet<string>,
): void {
  // Turning on skips picker-hidden leaves (e.g. project-scoped fiberLitBuildings outside a
  // project) so a row the user can't see never gets visibility persisted behind it. Turning
  // off stays wholesale, matching turnOffAll: switching a hidden leaf off never creates
  // invisible-on state.
  const presetLayerIds = PRESET_LAYERS[preset]();
  const layerIds = action === 'on' ? presetLayerIds.filter((id) => !hiddenLeafIds?.has(id)) : presetLayerIds;
  capturePosthogEvent(POSTHOG_EVENTS.layerPresetToggled, {
    preset,
    action,
    layer_ids: layerIds,
  });
  layerConfigStore.setSectionVisible(layerIds, action === 'on');
}

export function handleLayerIsolate(layerId: string): void {
  capturePosthogEvent(POSTHOG_EVENTS.layerIsolated, { layer_id: layerId });
  layerConfigStore.isolateLayer(layerId);
}

/** Undo the last isolate, restoring the pre-isolate visibility state. */
export function handleLayerIsolateUndo(layerId: string): void {
  capturePosthogEvent(POSTHOG_EVENTS.layerIsolateUndone, { layer_id: layerId });
  layerConfigStore.undoIsolate();
}

/** Bookmark / un-bookmark a layer (picker pin). `isPinned` is the state before the toggle. */
export function handleLayerPinToggle(layerId: string, isPinned: boolean): void {
  if (isPinned) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.layerUnpinned, { layer_id: layerId });
  } else {
    captureTypedPosthogEvent(POSTHOG_EVENTS.layerPinned, { layer_id: layerId });
  }
  layerConfigStore.togglePin(layerId, isPinned);
}

/** Expand / collapse a TOC section in the picker. `isExpanded` is the state before the toggle. */
export function handleSectionExpandToggle(sectionKey: string, isExpanded: boolean): void {
  captureTypedPosthogEvent(POSTHOG_EVENTS.layerSectionExpanded, {
    section_id: sectionKey,
    expanded: !isExpanded,
  });
  layerConfigStore.setExpanded(sectionKey, !isExpanded);
}

/** Expand / collapse every picker section (the global menu's "Expand/Collapse All Sections"). */
export function handleAllSectionsExpandToggle(expanded: boolean): void {
  layerConfigStore.setAllExpanded(expanded);
}
